import React, { useState, useRef, useEffect } from "react";
import { X, Maximize, Minimize, Plus, History, MessageCircle, Loader2, Pen, Copy, ThumbsUp, ThumbsDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { ChatInputBar } from "@/components/chat/chat-input/ChatInputBar";
import { supabase } from "@/integrations/supabase/client";
import { FeedbackPopover } from "@/components/FeedbackPopover";
import { useAuth } from "@/hooks/useAuth";
import type { Plan } from "@/lib/ai/models";
import { resolveModel } from "@/lib/ai/models";

export interface QuestionContext {
  questionIndex: number;
  questionText: string;
}

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ExamChatPanelProps {
  onClose: () => void;
  questionContext?: QuestionContext | null;
  onClearContext?: () => void;
}

export const ExamChatPanel: React.FC<ExamChatPanelProps> = ({
  onClose,
  questionContext,
  onClearContext,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedModel, setSelectedModel] = useState("auto");
  const [userPlan, setUserPlan] = useState<Plan>("free");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [likedMessages, setLikedMessages] = useState<Set<number>>(new Set());
  const { user } = useAuth();

  // Fetch subscription status
  useEffect(() => {
    if (!user) return;
    const fetchPlan = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_status")
        .eq("id", user.id)
        .single();
      if (data?.subscription_status === "active") {
        setUserPlan("pro");
      }
    };
    fetchPlan();
  }, [user]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleNewConversation = () => {
    setMessages([]);
    onClearContext?.();
  };

  const handleSendMessage = async (text: string, attachments: File[]) => {
    const trimmedInput = text.trim();
    if (!trimmedInput || isStreaming) return;

    // Build the AI message with context, but display only user's raw text
    let aiMessageContent = trimmedInput;
    if (questionContext) {
      aiMessageContent = `Regarding Question ${questionContext.questionIndex + 1}: "${questionContext.questionText}"\n\nMy question: ${trimmedInput}`;
    }

    const userMessage: Message = { role: "user", content: trimmedInput };
    setMessages(prev => [...prev, userMessage]);
    setIsStreaming(true);

    // Clear context after sending
    onClearContext?.();

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      // Get the user's access token for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("No active session");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: [
              {
                role: "system",
                content: `You are a helpful AI tutor assisting a student with their exam results. 
Your role is to:
- Explain concepts they got wrong
- Help them understand the correct answers
- Provide additional context and examples
- Be encouraging and supportive
- Keep explanations clear and concise

Format your responses with markdown for better readability.`,
              },
              ...messages.map(m => ({ role: m.role, content: m.content })),
              { role: "user", content: aiMessageContent },
            ],
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get response: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let assistantContent = "";

      // Add empty assistant message that we'll update
      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();
            if (data === "[DONE]") continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content;
              if (content) {
                assistantContent += content;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = {
                    role: "assistant",
                    content: assistantContent,
                  };
                  return updated;
                });
              }
            } catch {
              // Ignore parse errors for incomplete JSON
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        // Request was cancelled, don't show error
        return;
      }
      console.error("Chat error:", error);
      toast.error("Failed to get AI response. Please try again.");
      // Remove the empty assistant message if there was an error
      setMessages(prev => {
        if (prev.length > 0 && prev[prev.length - 1].role === "assistant" && !prev[prev.length - 1].content) {
          return prev.slice(0, -1);
        }
        return prev;
      });
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const handleCopyMessage = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <>
      {/* Mobile/Tablet backdrop */}
      <div 
        className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm xl:hidden"
        onClick={onClose}
        aria-label="Close chat backdrop"
      />
      
      <aside 
        className={`fixed right-0 top-0 z-50 h-screen border-l border-primary/5 bg-background transition-all duration-200 ease-in-out ${
          isFullscreen 
            ? 'w-full border-l-0' 
            : 'w-full lg:w-[400px] xl:w-[480px] 2xl:w-[576px]'
        }`}
        style={{
          boxShadow: isFullscreen 
            ? 'none' 
            : '-10px 0 40px -10px rgba(0, 0, 0, 0.1), -5px 0 20px -5px rgba(0, 0, 0, 0.05)'
        }}
      >
        <div className="relative flex h-full flex-col overflow-y-auto px-1 pb-1 pt-4">
          <div className="mb-7 ml-4 mr-2 flex items-center justify-between">
            <div className="text-muted-foreground transition-opacity duration-300 ease-in-out hover:text-foreground">
              <X
                onClick={onClose}
                className="h-5 w-5 cursor-pointer"
                aria-label="Close"
              />
            </div>

            <div className="text-muted-foreground transition-opacity duration-300 ease-in-out hover:text-foreground">
              <button 
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-transparent px-3 text-sm font-medium text-primary ring-offset-background transition-colors hover:bg-transparent focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? (
                  <Minimize className="h-4 w-4" />
                ) : (
                  <Maximize className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div className="flex-grow overflow-y-auto scrollbar-hide">
            <div className="relative flex h-full w-full flex-col px-0 md:h-[calc(100vh-84px)]">
              <div className="absolute right-0 top-0 z-40 mt-1 xl:-mt-2">
                <TooltipProvider>
                  <div className="flex items-center">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={handleNewConversation}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-primary/60 ring-offset-background transition-colors hover:bg-primary/5 hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                          aria-label="New conversation"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent><p>New Chat</p></TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl px-4 py-2 text-primary/60 ring-offset-background transition-colors hover:bg-primary/5 hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
                          aria-label="History"
                        >
                          <History className="h-3.5 w-3.5 shrink-0" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent><p>Chat History</p></TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              </div>

              <div data-chat-container="true" className="flex flex-1 flex-col overflow-y-auto overscroll-y-none" style={{ overflowAnchor: 'none' }}>
                <div className="mx-auto w-full max-w-3xl"></div>
                
                {messages.length === 0 ? (
                  <div className="mx-auto mt-10 flex w-full max-w-3xl flex-grow flex-col justify-center px-4 sm:px-4">
                    <div className="flex flex-col items-center justify-center p-4 text-center text-primary/40 dark:text-primary/60">
                      <MessageCircle className="mb-4 h-12 w-12 opacity-40" />
                      <p className="mb-1 text-base font-medium text-primary/40 dark:text-primary/60">
                        Discuss your exam results
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mx-auto flex w-full max-w-3xl flex-grow flex-col px-4 sm:px-4 mt-0 justify-start">
                    <div className="w-full">
                      <div className="space-y-8">
                        {messages.map((message, index) => (
                          <div key={index} className="flex flex-col gap-2">
                            {message.role === "user" ? (
                              /* User Message */
                              <div className="flex w-full justify-end">
                                <div className="group/message flex w-full flex-col items-end gap-1">
                                  <div className="flex w-full justify-end">
                                    <div className="relative w-fit rounded-3xl p-3 text-left leading-relaxed text-primary/95 border border-primary/5 bg-primary/5 dark:bg-neutral-800">
                                      <div className="markdown-body prose prose-neutral dark:prose-invert max-w-none">
                                        <div className="space-y-4">
                                          <p className="text-base leading-7 last:mb-0 whitespace-pre-wrap">{message.content}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 opacity-100 transition-opacity duration-200 lg:opacity-0 lg:group-hover/message:opacity-100">
                                    <button 
                                      className="rounded-full p-1 transition-colors hover:bg-primary/10" 
                                      aria-label="Edit message"
                                    >
                                      <Pen className="h-3 w-3 text-primary/60" aria-hidden="true" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              /* AI Message */
                              <div className="overflow-x-auto">
                                <div className="flex w-full flex-col">
                                  <div className="w-full">
                                    <div className="flex w-full justify-start">
                                      <div className="w-full" style={{ position: "relative" }}>
                                        <div className="relative rounded-3xl text-left leading-relaxed text-primary/95 w-full group bg-transparent p-0 pt-1">
                                          <div className="markdown-body prose prose-neutral dark:prose-invert max-w-none">
                                            <div className="space-y-4 inline">
                                              <ReactMarkdown
                                                components={{
                                                  p: ({ children }) => (
                                                    <p className="text-base leading-7 last:mb-0">{children}</p>
                                                  ),
                                                  h2: ({ children }) => (
                                                    <h2 className="mb-3 mt-6 text-xl font-semibold first:mt-0">{children}</h2>
                                                  ),
                                                  h3: ({ children }) => (
                                                    <h3 className="mb-2 mt-5 text-lg font-semibold first:mt-0">{children}</h3>
                                                  ),
                                                  ul: ({ children }) => (
                                                    <ul className="list-outside list-disc pl-5">{children}</ul>
                                                  ),
                                                  ol: ({ children }) => (
                                                    <ol className="list-outside list-decimal pl-5">{children}</ol>
                                                  ),
                                                  li: ({ children }) => (
                                                    <li className="leading-7">{children}</li>
                                                  ),
                                                  blockquote: ({ children }) => (
                                                    <blockquote className="my-6 rounded-r-md border-l-4 border-border/50 bg-muted/30 px-6 py-3 dark:border-primary/10">{children}</blockquote>
                                                  ),
                                                  hr: () => (
                                                    <hr className="my-8 border-t border-border/50 dark:border-primary/10" />
                                                  ),
                                                  strong: ({ children }) => (
                                                    <strong className="font-semibold">{children}</strong>
                                                  ),
                                                  code: ({ children }) => (
                                                    <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono">{children}</code>
                                                  ),
                                                }}
                                              >
                                                {message.content || "..."}
                                              </ReactMarkdown>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  {/* Action buttons for AI messages */}
                                  {message.content && (
                                    <div className="py-3">
                                      <div className="flex flex-row items-center justify-between space-x-1">
                                        <div className="flex flex-row items-center space-x-1">
                                          <button 
                                            onClick={() => handleCopyMessage(message.content)}
                                            className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 p-1.5 text-muted-foreground"
                                            aria-label="Copy message"
                                          >
                                            <Copy className="h-4 w-4" aria-hidden="true" />
                                          </button>
                                          <button 
                                            onClick={() => {
                                              setLikedMessages(prev => new Set(prev).add(index));
                                              toast.success("Thanks for your feedback");
                                            }}
                                            className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none hover:bg-accent hover:text-accent-foreground h-7 w-7 p-1.5 text-muted-foreground"
                                            aria-label="Like response"
                                          >
                                            <ThumbsUp className="h-4 w-4" fill={likedMessages.has(index) ? "currentColor" : "none"} aria-hidden="true" />
                                          </button>
                                          <FeedbackPopover>
                                            <button 
                                              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none hover:bg-accent hover:text-accent-foreground h-7 w-7 p-1.5 text-muted-foreground"
                                              aria-label="Dislike response"
                                            >
                                              <ThumbsDown className="h-4 w-4" aria-hidden="true" />
                                            </button>
                                          </FeedbackPopover>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        {isStreaming && messages[messages.length - 1]?.role === "assistant" && !messages[messages.length - 1]?.content && (
                          <div className="flex justify-start">
                            <div className="bg-transparent p-0 pt-1">
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mx-auto mt-0 w-full max-w-3xl px-2 sm:px-0">
                <ChatInputBar
                  onSendMessage={handleSendMessage}
                  disabled={isStreaming}
                  placeholder={questionContext ? "Ask about this question" : "Ask about your results"}
                  contextReference={questionContext ? {
                    text: `Question ${questionContext.questionIndex + 1}: ${questionContext.questionText}`,
                    onClear: () => onClearContext?.(),
                  } : undefined}
                  selectedModel={selectedModel}
                  onModelChange={setSelectedModel}
                  showAttachments={false}
                  userPlan={userPlan}
                />
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
